/**
 * utils/categories.js
 * Pure helpers for the managed categories list. A category is { name, code }.
 * Items reference a category by item.category (name). Name is freely renamable (rewrites
 * item.category in sync); code is identity and may change ONLY while no item uses the category.
 * Delete only when unused. Comparisons case-insensitive; canonical spelling preserved.
 * Names are normalized on store: trim + collapse internal whitespace to single spaces (same
 * rule as recipe names). The collision check compares normalized+lowercased forms on both sides.
 * Only data.categories and data.items[].category are touched; everything else passes through.
 */

/** Trim + collapse internal whitespace (case preserved). Applied on every name write/compare. */
const tidy = (s) => String(s || "").trim().replace(/\s+/g, " ");

/** Number of items whose category === name (case-insensitive, tidy-normalized). */
export function countItemsUsingCategory(data, name) {
  const low = tidy(name).toLowerCase();
  return (data.items || []).filter((it) => tidy(it.category).toLowerCase() === low).length;
}

/**
 * Rename a category and optionally change its code.
 * - newName blank (after tidy) → no-op.
 * - newName, after tidy+lower, collides with a DIFFERENT existing category → no-op
 *   (refuses to merge; caller validates and shows an error).
 * - newCode: applied ONLY when the category currently has 0 items (locked when used).
 * - Rewrites every item.category matching oldName (tidy+lower) to the new canonical name.
 * - Stored name is tidy(newName): trimmed + internal whitespace collapsed.
 */
export function renameCategory(data, oldName, newName, newCode) {
  const oldLow = tidy(oldName).toLowerCase();
  const nm = tidy(newName);
  if (!nm) return data;

  const cats = data.categories || [];
  const target = cats.find((c) => tidy(c.name).toLowerCase() === oldLow);
  if (!target) return data;

  // Block rename into an existing different category name (normalized comparison).
  if (nm.toLowerCase() !== oldLow && cats.some((c) => tidy(c.name).toLowerCase() === nm.toLowerCase())) {
    return data;
  }

  const used = countItemsUsingCategory(data, oldName) > 0;
  const nextCode = (newCode != null && !used)
    ? String(newCode).trim().toUpperCase()
    : target.code;

  // If changing the code, block a collision with another category's code.
  if (nextCode !== target.code && cats.some((c) => c.code.toUpperCase() === nextCode && tidy(c.name).toLowerCase() !== oldLow)) {
    return data;
  }

  const categories = cats.map((c) =>
    tidy(c.name).toLowerCase() === oldLow ? { name: nm, code: nextCode } : c
  );
  const items = (data.items || []).map((it) =>
    tidy(it.category).toLowerCase() === oldLow ? { ...it, category: nm } : it
  );
  return { ...data, categories, items };
}

/** Remove a category ONLY if unused (0 items). Otherwise no-op. */
export function deleteCategory(data, name) {
  if (countItemsUsingCategory(data, name) > 0) return data;
  const low = tidy(name).toLowerCase();
  const categories = (data.categories || []).filter((c) => tidy(c.name).toLowerCase() !== low);
  return { ...data, categories };
}
